import {
  ACTION_STATUS_LABELS,
  SOURCE_LABELS,
  STATUS_LABELS,
  VERDICT_LABELS,
} from '../api/types';
import type {
  ActionStatus,
  AlertSource,
  CaseStatus,
  Mode,
  Severity,
  Verdict,
} from '../api/types';
import { Badge } from './Badge';
import type { Tone } from './Badge';

/**
 * Domain badges. One mapping per enum, in one file.
 *
 * Centralised so `high` is the same colour on the dashboard, the case list and the case page.
 * Brand orange is deliberately not used for any severity: it is the navigation and primary
 * action colour, and reusing it for `high` would make the two unreadable together.
 */

const SEVERITY_TONES: Record<Severity, Tone> = {
  info: 'info',
  low: 'neutral',
  medium: 'amber',
  high: 'danger',
  critical: 'danger',
};

export function SeverityBadge({ severity }: { severity: Severity }) {
  return (
    <Badge tone={SEVERITY_TONES[severity]} emphasis={severity === 'critical'}>
      {severity === 'critical' ? 'CRITICAL' : severity.toUpperCase()}
    </Badge>
  );
}

const STATUS_TONES: Record<CaseStatus, Tone> = {
  open: 'info',
  enriching: 'violet',
  awaiting_analysis: 'violet',
  // The queue a human has to clear. Amber so it reads as waiting-on-you, not as failed.
  awaiting_approval: 'warning',
  containing: 'teal',
  contained: 'success',
  closed_benign: 'neutral',
  closed_resolved: 'success',
  failed: 'danger',
};

export function StatusBadge({ status }: { status: CaseStatus }) {
  return <Badge tone={STATUS_TONES[status]}>{STATUS_LABELS[status]}</Badge>;
}

const VERDICT_TONES: Record<Verdict, Tone> = {
  benign: 'success',
  suspicious: 'warning',
  compromise_likely: 'danger',
  compromise_confirmed: 'danger',
  // Grey, not amber: this says the collection failed, not that the activity is ambiguous.
  // It never leads to containment, and colouring it like a finding would misread it.
  insufficient_evidence: 'neutral',
};

export function VerdictBadge({ verdict }: { verdict: Verdict }) {
  return (
    <Badge tone={VERDICT_TONES[verdict]} emphasis={verdict === 'compromise_confirmed'}>
      {VERDICT_LABELS[verdict]}
    </Badge>
  );
}

const ACTION_STATUS_TONES: Record<ActionStatus, Tone> = {
  recommended: 'neutral',
  pending_approval: 'warning',
  approved: 'info',
  rejected: 'neutral',
  executing: 'teal',
  succeeded: 'success',
  failed: 'danger',
  // Nobody approved it in time. Escalated rather than dropped, so it is not grey.
  expired: 'amber',
  blocked: 'danger',
};

export function ActionStatusBadge({ status }: { status: ActionStatus }) {
  return <Badge tone={ACTION_STATUS_TONES[status]}>{ACTION_STATUS_LABELS[status]}</Badge>;
}

const MODE_TONES: Record<Mode, Tone> = {
  disabled: 'neutral',
  approve: 'warning',
  auto: 'teal',
};

const MODE_TITLES: Record<Mode, string> = {
  disabled: 'Policy has this action disabled. It is recorded and never executed.',
  approve: 'A second responder must approve before this runs.',
  auto: 'Policy permits this to run without human approval.',
};

export function ModeBadge({ mode }: { mode: Mode }) {
  return (
    <Badge tone={MODE_TONES[mode]} title={MODE_TITLES[mode]}>
      {mode}
    </Badge>
  );
}

export function SourceBadge({ source }: { source: AlertSource }) {
  return <Badge tone="sky">{SOURCE_LABELS[source] ?? source}</Badge>;
}
